import { useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface UserProfile {
  id: number;
  wallet_address: string;
  wins: number;
  draws: number;
  losses: number;
  max_win_streak: number;
  current_win_streak: number;
  discord_username?: string;
}

export function ProfileModal({ isOpen, onClose }: ProfileModalProps) {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [discordUsername, setDiscordUsername] = useState<string>('');
  const [showCheckmark, setShowCheckmark] = useState<boolean>(false);
  const [isCheckmarkFading, setIsCheckmarkFading] = useState<boolean>(false);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [shouldRender, setShouldRender] = useState<boolean>(false);

  // Блокировка прокрутки страницы при открытом модальном окне
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Очистка при размонтировании компонента
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const { publicKey, connected } = useWallet();

  const fetchProfile = async () => {
    if (!publicKey) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/wallet-stats/${publicKey.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setDiscordUsername(data.discord_username || '');
      } else if (response.status === 404) {
        // User not in database yet
        setProfile(null);
        setDiscordUsername('');
      } else {
        setError('Failed to load profile data');
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const saveDiscordUsername = async () => {
    if (!publicKey || !connected) return;

    setSaving(true);
    setError(null);
    setShowCheckmark(false);
    setIsCheckmarkFading(false);

    try {
      const response = await fetch('/api/update-discord-username', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          wallet_address: publicKey.toString(),
          discord_username: discordUsername.trim(),
        }),
      });

      if (response.ok) {
        const updatedProfile = await response.json();
        setProfile(updatedProfile);
        setShowCheckmark(true);
        setIsCheckmarkFading(false);
        setTimeout(() => {
          setIsCheckmarkFading(true);
          setTimeout(() => setShowCheckmark(false), 500); // Время на анимацию исчезновения
        }, 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save Discord username');
      }
    } catch (err) {
      console.error('Error saving Discord username:', err);
      setError('Failed to save Discord username');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (isOpen && connected) {
      fetchProfile();
    }
  }, [isOpen, connected, publicKey]);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Small delay to ensure DOM is ready before animation
      const timer = setTimeout(() => setIsAnimating(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => setShouldRender(false), 150);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);


  if (!shouldRender) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
      <style>{`
        .modal-content {
          opacity: 0;
          transform: scale(0.5);
          transition: all 0.15s ease-out;
        }

        .modal-content.entering {
          opacity: 1;
          transform: scale(1);
        }

        .modal-content.exiting {
          opacity: 0;
          transform: scale(0.5);
        }
      `}</style>
      <div
        className={`bg-black/80 rounded-xl shadow-xl border border-purple-800/50 p-6 max-w-md w-full mx-4 overflow-hidden animate-border-glow modal-content ${
          isAnimating ? 'entering' : 'exiting'
        }`}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white gradient-text">Profile</h2>
          <button 
            onClick={onClose}
            className="text-purple-400 hover:text-white transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {!connected ? (
          <div className="text-center py-8">
            <div className="text-gray-400 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto mb-4 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 0h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <p className="text-gray-400 text-lg mb-2">Wallet not connected</p>
            <p className="text-gray-500 text-sm">Please connect your wallet to view your profile</p>
          </div>
        ) : loading ? (
          <div className="flex justify-center items-center py-8">
            <p className="text-purple-400">Loading profile...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Wallet Info */}
            <div className={`glass-card p-4 rounded-lg border ${connected ? 'border-purple-800/50 bg-purple-900/20' : 'border-gray-600 bg-gray-800/20'}`}>
              <h3 className="text-lg font-semibold text-purple-300 mb-3">Wallet Information</h3>
              <div className="space-y-2">
                <div className="flex flex-col gap-1">
                  <span className={connected ? 'text-purple-200' : 'text-gray-400'}>Address:</span>
                  <span className={`font-mono text-sm break-all ${connected ? 'text-purple-300' : 'text-gray-500'}`}>
                    {connected ? publicKey!.toString() : 'Not connected'}
                  </span>
                </div>
              </div>
            </div>

            {/* Game Statistics */}
            <div className={`glass-card p-4 rounded-lg border ${connected && profile && profile.id ? 'border-purple-800/50 bg-purple-900/20' : 'border-gray-600 bg-gray-800/20'}`}>
              <h3 className={`text-lg font-semibold mb-3 ${connected && profile && profile.id ? 'text-purple-300' : 'text-gray-400'}`}>Game Statistics</h3>
              {connected && profile && profile.id ? (
                <div className="space-y-4">
                  {/* Основные статистики */}
                  <div className="grid grid-cols-3 gap-2">
                    <div className="glass-card bg-purple-900/10 p-3 rounded-lg border border-purple-800/30 hover:border-purple-700/50 transition-all duration-100 ease-out">
                      <div className="flex flex-col items-center space-y-1">
                        <div className="w-6 h-6 flex items-center justify-center">
                          <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="text-lg font-bold text-purple-200">{profile.wins}</div>
                        <div className="text-xs text-purple-300 font-medium">Wins</div>
                      </div>
                    </div>

                    <div className="glass-card bg-purple-900/10 p-3 rounded-lg border border-purple-800/30 hover:border-purple-700/50 transition-all duration-100 ease-out">
                      <div className="flex flex-col items-center space-y-1">
                        <div className="w-6 h-6 rounded-full bg-yellow-600/20 flex items-center justify-center">
                          <svg className="w-3 h-3 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M20 4H4" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M20 12H4" />
                          </svg>
                        </div>
                        <div className="text-lg font-bold text-purple-200">{profile.draws}</div>
                        <div className="text-xs text-purple-300 font-medium">Draws</div>
                      </div>
                    </div>

                    <div className="glass-card bg-purple-900/10 p-3 rounded-lg border border-purple-800/30 hover:border-purple-700/50 transition-all duration-100 ease-out">
                      <div className="flex flex-col items-center space-y-1">
                        <div className="w-6 h-6 rounded-full bg-red-600/20 flex items-center justify-center">
                          <svg className="w-3 h-3 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="text-lg font-bold text-purple-200">{profile.losses}</div>
                        <div className="text-xs text-purple-300 font-medium">Losses</div>
                      </div>
                    </div>
                  </div>

                  {/* Дополнительная статистика */}
                  {(() => {
                    const totalGames = profile.wins + profile.draws + profile.losses;
                    const winRate = totalGames > 0 ? Math.round((profile.wins / totalGames) * 100) : 0;
                    return totalGames > 0 ? (
                      <div className="grid grid-cols-2 gap-2 pt-1">
                        <div className="glass-card bg-purple-900/10 p-3 rounded-lg border border-purple-800/30 hover:border-purple-700/50 transition-all duration-100 ease-out text-center">
                          <div className="text-lg font-bold text-purple-200">{totalGames}</div>
                          <div className="text-xs text-purple-300 font-medium">Total Games</div>
                        </div>
                        <div className="glass-card bg-purple-900/10 p-3 rounded-lg border border-purple-800/30 hover:border-purple-700/50 transition-all duration-100 ease-out text-center">
                          <div className="text-lg font-bold text-purple-200">{winRate}%</div>
                          <div className="text-xs text-purple-300 font-medium">Win Rate</div>
                        </div>
                      </div>
                    ) : null;
                  })()}

                  {/* Win Streaks */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="glass-card bg-purple-900/10 p-3 rounded-lg border border-purple-800/30 hover:border-purple-700/50 transition-all duration-100 ease-out">
                      <div className="flex flex-col items-center space-y-1">
                        <div className="w-6 h-6 rounded-full bg-orange-600/20 flex items-center justify-center">
                          <svg className="w-3 h-3 text-orange-400" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        </div>
                        <div className="text-lg font-bold text-purple-200">{profile.max_win_streak}</div>
                        <div className="text-xs text-purple-300 font-medium">Max Streak</div>
                      </div>
                    </div>

                    <div className="glass-card bg-purple-900/10 p-3 rounded-lg border border-purple-800/30 hover:border-purple-700/50 transition-all duration-100 ease-out">
                      <div className="flex flex-col items-center space-y-1">
                        <div className="w-6 h-6 rounded-full bg-yellow-600/20 flex items-center justify-center">
                          <svg className="w-3 h-3 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="text-lg font-bold text-purple-200">{profile.current_win_streak}</div>
                        <div className="text-xs text-purple-300 font-medium">Current Streak</div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <div className="w-16 h-16 rounded-full bg-purple-800/20 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <p className="text-gray-400 text-sm">
                    {!connected ? 'Connect wallet to view stats' :
                     profile && profile.discord_username ? 'No games played yet' :
                     'No games played yet'}
                  </p>
                </div>
              )}
            </div>

            {/* Discord Username */}
            <div className={`glass-card p-4 rounded-lg border ${connected ? 'border-purple-800/50 bg-purple-900/20' : 'border-gray-600 bg-gray-800/20'}`}>
              <h3 className={`text-lg font-semibold mb-3 ${connected ? 'text-purple-300' : 'text-gray-400'}`}>Discord Username</h3>
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="Enter your Discord username"
                  value={discordUsername}
                  onChange={(e) => setDiscordUsername(e.target.value)}
                  disabled={!connected || saving}
                  style={{ backgroundColor: 'black' }}
                  className={`w-full px-3 py-2 rounded-lg border text-sm
                    ${connected
                      ? '!bg-black border-purple-800/50 text-purple-200 placeholder-purple-400/70 focus:outline-none focus:ring-2 focus:ring-purple-500/50'
                      : '!bg-black border-gray-600 text-gray-400 placeholder-gray-500 cursor-not-allowed'
                    }`}
                />
                <button
                  onClick={saveDiscordUsername}
                  disabled={!connected || saving || !discordUsername.trim() || discordUsername.trim() === (profile?.discord_username || '')}
                  className={`relative w-full py-2 px-4 font-semibold text-sm flex items-center justify-center border-2 border-purple-500/80
                    ${connected && discordUsername.trim() && !saving && discordUsername.trim() !== (profile?.discord_username || '')
                      ? 'btn-textured text-white'
                      : 'btn-textured-secondary text-purple-400 cursor-not-allowed opacity-50'
                    }`}
                >
                  <span>{saving ? 'Saving...' : 'Save Discord Username'}</span>
                  {showCheckmark && (
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      className="absolute right-3 h-4 w-4 text-green-400" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                      style={{
                        animation: isCheckmarkFading 
                          ? 'fadeOutScale 0.5s ease-out forwards' 
                          : 'fadeInScale 0.5s ease-out forwards'
                      }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Error Messages */}
            {error && (
              <div className="p-3 rounded-lg bg-red-900/30 text-red-300 border border-red-700/50 text-sm">
                {error}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
